import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout.tsx';
import { RequireAuth } from './features/auth/RequireAuth.tsx';
import { queryClient } from './lib/query-client.ts';

/**
 * Top-level routing.
 *
 * All page components are lazy-loaded so the initial bundle stays small —
 * landing on `/` doesn't pull in the editor pages, the panorama viewer's
 * @egjs/view360 chunk, or the auth flow's react-hook-form code. Each
 * page becomes its own chunk and Vite's automatic code-splitting handles
 * the rest. Initial JS dropped from ~230 KB → ~80 KB gzip after this.
 *
 * The Layout itself stays eager because the header is always visible
 * during route transitions; we only show <PageFallback /> for the body.
 *
 * `/dev2/*` paths from v1 redirect to `/dev/*` so old bookmarks survive
 * the rename.
 */

// Eager: NotFoundPage is small and rendered for unknown paths.
import NotFoundPage from './routes/NotFoundPage.tsx';

// Public
const HomePage = lazy(() => import('./routes/HomePage.tsx'));
const GalleryPage = lazy(() => import('./routes/GalleryPage.tsx'));
const ViewPage = lazy(() => import('./routes/ViewPage.tsx'));

// Auth
const SignInPage = lazy(() => import('./routes/auth/SignInPage.tsx'));
const SignUpPage = lazy(() => import('./routes/auth/SignUpPage.tsx'));
const ConfirmSignUpPage = lazy(() => import('./routes/auth/ConfirmSignUpPage.tsx'));
const ForgotPasswordPage = lazy(() => import('./routes/auth/ForgotPasswordPage.tsx'));
const ResetPasswordPage = lazy(() => import('./routes/auth/ResetPasswordPage.tsx'));

// Authenticated user pages
const ProfilePage = lazy(() => import('./routes/me/ProfilePage.tsx'));

// Authenticated editor pages
const DashboardPage = lazy(() => import('./routes/dev/DashboardPage.tsx'));
const GalleryEditorPage = lazy(() => import('./routes/dev/GalleryEditorPage.tsx'));
const ViewEditorPage = lazy(() => import('./routes/dev/ViewEditorPage.tsx'));
const ItemEditorPage = lazy(() => import('./routes/dev/ItemEditorPage.tsx'));

function PageFallback() {
  // Intentionally minimal — the visible header is rendered by Layout
  // outside the Suspense boundary, so a loading spinner here would be
  // a duplicate cue. Returning null keeps transitions snappy.
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route
              index
              element={
                <Suspense fallback={<PageFallback />}>
                  <HomePage />
                </Suspense>
              }
            />

            {/* Public viewer paths */}
            <Route
              path="gallery/:galleryId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <GalleryPage />
                </Suspense>
              }
            />
            <Route
              path="gallery/:galleryId/view/:viewId"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ViewPage />
                </Suspense>
              }
            />

            {/* Auth */}
            <Route
              path="signin"
              element={
                <Suspense fallback={<PageFallback />}>
                  <SignInPage />
                </Suspense>
              }
            />
            <Route
              path="signup"
              element={
                <Suspense fallback={<PageFallback />}>
                  <SignUpPage />
                </Suspense>
              }
            />
            <Route
              path="confirm-signup"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ConfirmSignUpPage />
                </Suspense>
              }
            />
            <Route
              path="forgot-password"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="reset-password"
              element={
                <Suspense fallback={<PageFallback />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />

            {/* Authenticated user pages */}
            <Route
              path="me"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <ProfilePage />
                  </Suspense>
                </RequireAuth>
              }
            />

            {/* Authenticated editor pages */}
            <Route
              path="dev"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <DashboardPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="dev/gallery/new"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <GalleryEditorPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="dev/gallery/:galleryId"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <GalleryEditorPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="dev/gallery/:galleryId/view/:viewId"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <ViewEditorPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="dev/item/new"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <ItemEditorPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="dev/item/:itemId"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageFallback />}>
                    <ItemEditorPage />
                  </Suspense>
                </RequireAuth>
              }
            />

            {/* v1 → v2 path redirects */}
            <Route path="dev2" element={<Navigate to="/dev" replace />} />
            <Route path="dev2/*" element={<Navigate to="/dev" replace />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
